import 'react-phone-number-input/style.css'

import classNames from 'classnames'
import React, { useContext } from 'react'
import PhoneInput from 'react-phone-number-input'

import { Intent } from '../../config/intent'
import { PROFILE_CONTEXT } from '../../contexts/settings/profile'
import { Button } from '../button'
import { DOBInput } from '../dob-input'
import { Input } from '../input'
import { AvailableRolesSelect } from './available-roles-select'
import { CountrySelect } from './country-select'
import { ProductInterestsSelect } from './product-interests-select'
// import { RoleSelect } from './role-select'

export const I18n = {
    HEADING: 'Profile information',
    SUB_HEADING:
        'Please provide the following information so we can provide the best experience for you',
    FIRST_NAME_LABEL: 'First name*',
    LAST_NAME_LABEL: 'Last name*',
    DOB_LABEL: 'Date of birth*',
    PRODUCT_INTEREST_LABEL: 'What products are you interested in?*',
    COUNTRY_LABEL: 'Country of residence?*',
    SUBMIT: 'Edit',
    FOOTER_MESSAGE: 'Your info is safely secured and encrypted',
    EMAIL_LABEL: 'Email',
    PHONE_LABEL: 'Phone',
}

export const TestId = {
    FORM: 'profile-info-form',
}

export interface ProfileInfoFormValues {
    firstName: string
    lastName: string
    dateOfBirth: string
    productInterests: string[]
    country: string
    email?: string
    phone?: string
    notifyPhone?: boolean
    notifyEmail?: boolean
    availableRoles?: any[]
    activeRole?: string
}

export interface ProfileInfoFormProps {
    onComplete?: (data: ProfileInfoFormValues) => void
    emailAndPhone?: boolean
}

export function ProfileInfoForm({
    onComplete,
    emailAndPhone = false,
}: ProfileInfoFormProps) {
    const [loading] = React.useState(false)
    const {
        onSubmit,
        control,
        errors,
        defaultValues,
        formData,
        updateFormData,
    }: any = useContext(PROFILE_CONTEXT)

    const containerStyles = classNames(
        'px-12 py-4 flex flex-col w-full overflow-auto',
    )
    const formContainerStyles = classNames('flex flex-col gap-y-2 flex-grow')

    const phoneContainerStyles = classNames(
        ' border border-gray rounded-md px-8 h-12 flex items-center text-gray',
        'focus-within:outline-none focus-within:ring focus-within:ring-black focus-within:shadow-md',
    )

    const headerStyles = classNames('border-b border-gray2 pb-6 mb-6')
    const headerTitleStyles = classNames('text-xl', {
        'font-semibold text-2xl': !emailAndPhone,
    })

    const inputContainerStyles = classNames('flex flex-col')
    const labelStyle = classNames('text-sm font-light text-default mb-2')

    return (
        <div className={containerStyles}>
            {emailAndPhone ? (
                <div className={headerStyles}>
                    <h4 className={headerTitleStyles}>Personal information</h4>
                </div>
            ) : (
                <div className={headerStyles}>
                    <h4 className={headerTitleStyles}>Profile information</h4>
                    <p>
                        Please provide the following information so we can
                        provide the best experience for you
                    </p>
                </div>
            )}

            <div className={formContainerStyles}>
                <div className={inputContainerStyles}>
                    <label htmlFor='firstName' className={labelStyle}>
                        First name
                    </label>
                    <Input
                        value={formData.firstName}
                        onChange={e =>
                            updateFormData({ firstName: e.target.value })
                        }
                        id='firstName'
                    />
                </div>
                <div className={inputContainerStyles}>
                    <label htmlFor='lastName' className={labelStyle}>
                        Last name
                    </label>
                    <Input
                        value={formData.lastName}
                        onChange={e =>
                            updateFormData({ lastName: e.target.value })
                        }
                        id='lastName'
                    />
                </div>
                {emailAndPhone && (
                    <>
                        <div className={inputContainerStyles}>
                            <label htmlFor='phone' className={labelStyle}>
                                Phone number
                            </label>
                            <PhoneInput
                                value={formData.phone}
                                onChange={(phone: string) =>
                                    updateFormData({ phone: phone })
                                }
                                defaultCountry='US'
                                className={phoneContainerStyles}
                            />
                        </div>

                        <div className={inputContainerStyles}>
                            <label htmlFor='email' className={labelStyle}>
                                Email
                            </label>
                            <Input
                                value={formData.email}
                                onChange={e =>
                                    updateFormData({ email: e.target.value })
                                }
                                id='email'
                            />
                        </div>
                    </>
                )}

                <div className={inputContainerStyles}>
                    <label htmlFor='country' className={labelStyle}>
                        Country of residence
                    </label>
                    <CountrySelect
                        value={formData.country}
                        onChange={value =>
                            updateFormData({
                                country: value.value,
                            })
                        }
                        intent={
                            errors.country?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        defaultValue={defaultValues?.country}
                    />
                </div>

                <div className={inputContainerStyles}>
                    <label htmlFor='dateOfBirth' className={labelStyle}>
                        Date of birth
                    </label>
                    <DOBInput
                        id='dateOfBirth'
                        value={formData.dateOfBirth}
                        onChange={e =>
                            updateFormData({
                                dateOfBirth: e.target.value,
                            })
                        }
                        intent={
                            errors.dateOfBirth?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                        message={errors.dateOfBirth?.message}
                    />
                </div>
            </div>
            {!emailAndPhone && (
                <>
                    <div className={inputContainerStyles}>
                        <label className={labelStyle}>Account type</label>
                        <AvailableRolesSelect
                            value={[]}
                            onChange={(value: any) => {
                                updateFormData({
                                    availableRoles: value,
                                })
                            }}
                            control={control}
                            intent={Intent.DEFAULT}
                        />
                    </div>
                    {/* <div className={`${inputContainerStyles} mt-2`}>
                    <label className={labelStyle}>Active Account type</label>
                    <RoleSelect
                        value={formData.activeRole}
                        onChange={(value: any, b: any) => {
                            updateFormData({
                                activeRole: value.value,
                            })
                        }}
                        availableRoles={formData.availableRoles}
                        control={control}
                        intent={
                            errors.activeRole?.message
                                ? Intent.DANGER
                                : Intent.DEFAULT
                        }
                    />
                </div> */}
                </>
            )}
            <div className={`${inputContainerStyles} mt-2`}>
                <label className={labelStyle}>What do you want to do?</label>
                <ProductInterestsSelect
                    value={formData.productInterests}
                    onChange={(value: any) => {
                        updateFormData({
                            productInterests: value,
                        })
                    }}
                    control={control}
                    intent={Intent.DEFAULT}
                />
            </div>
            {emailAndPhone ? (
                <div className='w-full pt-6 mt-6 border-t h-max border-gray2'>
                    <Button loading={loading} onClick={onSubmit(onComplete)}>
                        {I18n.SUBMIT}
                    </Button>
                </div>
            ) : (
                <div className='w-full mt-6 h-max border-tpt-6'>
                    <Button
                        loading={loading}
                        onClick={onSubmit(onComplete)}
                        className='py-4 text-xl'
                        fill>
                        Save & Continue
                    </Button>
                    <p className='mt-4 text-xs text-center'>
                        Your info is safely secured and encrypted
                    </p>
                </div>
            )}
        </div>
    )
}
